import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import useCommandPaletteStore from '../../stores/commandPaletteStore';
import useModuleStore from '../../stores/moduleStore';
import useUiStore from '../../stores/uiStore';
import { buildCommands, buildCommandUrl } from './commands';

/**
 * Haupt-Hook der Palette.
 * Liefert:
 *  - groupedResults: { section: Command[] } in Reihenfolge [Seiten, Erstellen, Aktionen]
 *  - flatResults:    Command[] mit MRU-Boost + Kontext-Boost
 *  - executeCommand(cmd)
 *  - sectionsOrder:  Array mit Reihenfolge der Sektionen
 *  - highlightIndex, moveHighlight, setHighlightIndex, closePalette, ...
 */
export default function useCommandPalette() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isEnabled, loaded } = useModuleStore();
  const uiStore = useUiStore();

  const {
    open,
    query,
    highlightIndex,
    mru,
    setHighlightIndex,
    moveHighlight,
    setQuery,
    openPalette,
    closePalette,
    togglePalette,
    pushMru,
  } = useCommandPaletteStore();

  // Commands neu ableiten, wenn Route/Module-Status wechselt.
  const commands = useMemo(
    () => buildCommands({ isEnabled, pathname: location.pathname }),
    // isEnabled ist vom Zustand abhaengig — loaded reicht als Trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, loaded]
  );

  // Fuse-Instanz cachen (teuer zu bauen, aber klein im Speicher).
  const fuseRef = useRef(null);
  useEffect(() => {
    fuseRef.current = new Fuse(commands, {
      threshold: 0.3,
      ignoreLocation: true,
      includeScore: true,
      keys: [
        { name: 'title', weight: 0.6 },
        { name: 'keywords', weight: 0.3 },
        { name: 'section', weight: 0.1 },
      ],
    });
  }, [commands]);

  // Gefilterte + sortierte Ergebnisse.
  const flatResults = useMemo(() => {
    const mruRank = new Map(mru.map((id, i) => [id, mru.length - i])); // hoher Score = weiter vorn
    let list;

    if (!query || !query.trim()) {
      // Ohne Query: Alle Commands, nach (MRU + ContextBoost + Sektion) sortiert.
      list = [...commands];
    } else if (fuseRef.current) {
      list = fuseRef.current.search(query).map((r) => ({ ...r.item, _fuseScore: r.score ?? 1 }));
    } else {
      list = [];
    }

    const sectionOrder = { Seiten: 0, Erstellen: 1, Aktionen: 2 };

    list.sort((a, b) => {
      const aMru = mruRank.get(a.id) || 0;
      const bMru = mruRank.get(b.id) || 0;
      if (aMru !== bMru) return bMru - aMru; // MRU first

      const aCtx = a._contextBoost || 0;
      const bCtx = b._contextBoost || 0;
      if (aCtx !== bCtx) return bCtx - aCtx;

      // Fuse-Score (nur wenn suche aktiv)
      if (a._fuseScore != null && b._fuseScore != null && a._fuseScore !== b._fuseScore) {
        return a._fuseScore - b._fuseScore;
      }

      const aSec = sectionOrder[a.section] ?? 99;
      const bSec = sectionOrder[b.section] ?? 99;
      if (aSec !== bSec) return aSec - bSec;

      return (a.title || '').localeCompare(b.title || '');
    });

    return list;
  }, [query, commands, mru]);

  const groupedResults = useMemo(() => {
    const grouped = { Seiten: [], Erstellen: [], Aktionen: [] };
    for (const cmd of flatResults) {
      if (!grouped[cmd.section]) grouped[cmd.section] = [];
      grouped[cmd.section].push(cmd);
    }
    return grouped;
  }, [flatResults]);

  const sectionsOrder = ['Seiten', 'Erstellen', 'Aktionen'];

  // ── Command ausfuehren ───────────────────────────
  function executeCommand(cmd) {
    if (!cmd) return;
    pushMru(cmd.id);

    try {
      if (typeof cmd.run === 'function') {
        cmd.run({ uiStore, navigate });
      }
      const url = buildCommandUrl(cmd);
      if (url) navigate(url);
    } finally {
      closePalette();
    }
  }

  // Bei Query-Change: Highlight auf 0 begrenzen (Store-Setter macht das schon,
  // aber bei MRU/Kontext-Wechsel muessen wir abfangen).
  useEffect(() => {
    if (highlightIndex >= flatResults.length) {
      setHighlightIndex(0);
    }
  }, [flatResults.length, highlightIndex, setHighlightIndex]);

  return {
    open,
    query,
    setQuery,
    openPalette,
    closePalette,
    togglePalette,
    highlightIndex,
    setHighlightIndex,
    moveHighlight,
    flatResults,
    groupedResults,
    sectionsOrder,
    executeCommand,
    mruIds: mru,
  };
}
